import { z } from 'zod';
import { EssayAttachment } from './session.js';
import { QuestionId, UserId } from './common.js';
export const ScoreEssayRequest = z.object({
    questionId: QuestionId,
    score: z.number().int().nonnegative(),
    feedback: z.string().max(2000).optional(),
});
export const GradingTotals = z.object({
    autoScore: z.number().nonnegative(),
    manualScore: z.number().nonnegative(),
    totalScore: z.number().nonnegative(),
    maxScore: z.number().nonnegative(),
});
/* ---------- AI essay grading assist (suggestion only) ---------- */
export const AiSuggestEssayRequest = z.object({ questionId: QuestionId });
export const AiSuggestionCriterion = z.object({
    label: z.string().min(1).max(200),
    maxScore: z.number().int().positive(),
    score: z.number().int().nonnegative(),
    rationale: z.string().min(1).max(1000),
});
/** A suggestion never becomes a grade on its own: the teacher still posts a score. */
export const AiSuggestion = z.object({
    criteria: z.array(AiSuggestionCriterion).min(1).max(10),
    totalScore: z.number().int().nonnegative(),
    maxScore: z.number().int().positive(),
    model: z.string(),
});
export const AiGradingAgreementReport = z.object({
    quizId: z.string(),
    suggested: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    overridden: z.number().int().nonnegative(),
    overrideRate: z.number().min(0).max(1).nullable(),
    exactAgreementRate: z.number().min(0).max(1).nullable(),
    meanAbsoluteScoreDifference: z.number().nonnegative().nullable(),
    meanAbsoluteDifferenceAsFractionOfMax: z.number().nonnegative().nullable(),
});
export const GradingEssayAttachment = EssayAttachment.extend({
    downloadUrl: z.string().url(),
});
export const GradingEssay = z.object({
    questionId: QuestionId,
    prompt: z.string(),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().optional(),
    responseText: z.string().nullable(),
    attachments: z.array(GradingEssayAttachment),
    score: z.number().int().nonnegative().optional(),
    feedback: z.string().nullable().optional(),
    gradedBy: UserId.optional(),
    aiSuggestion: AiSuggestion.optional(),
});
export const GradingSessionResponse = z.object({
    id: z.string(),
    studentName: z.string().optional(),
    studentNumber: z.string().nullable().optional(),
    status: z.enum(['submitted', 'graded']),
    quizTitle: z.string().optional(),
    classroomName: z.string().nullable().optional(),
    scheduleStartAt: z.string().nullable().optional(),
    scheduleEndAt: z.string().nullable().optional(),
    essays: z.array(GradingEssay),
    result: GradingTotals.optional(),
});
/* ---------- Quiz workflow status ---------- */
export const QuizWorkflowStatus = z.enum([
    'needs_grading',
    'ready_to_finalize',
    'ready_to_publish',
    'published',
]);
/* ---------- Grouped grading queue ---------- */
export const GradingQueueStudent = z.object({
    sessionId: z.string(),
    studentName: z.string().optional(),
    studentNumber: z.string().nullable().optional(),
    submittedAt: z.string().optional(),
    gradingStatus: z.enum(['pending', 'partial', 'graded']),
    published: z.boolean().optional(),
    /**
     * Raw session delivery status; absent while delivery has not been queued.
     * `published` collapses every terminal state into one flag, so the queue
     * needs this to tell a failed delivery (admin retry only) from one still
     * in flight.
     */
    resultDeliveryStatus: z.string().optional(),
});
export const GradingQueueGroup = z.object({
    quizId: z.string(),
    quizTitle: z.string(),
    classroomId: z.string().nullable(),
    classroomName: z.string().nullable(),
    scheduleStartAt: z.string().nullable(),
    scheduleEndAt: z.string().nullable(),
    pendingCount: z.number().int().nonnegative(),
    gradedCount: z.number().int().nonnegative(),
    publishedCount: z.number().int().nonnegative(),
    quizWorkflowStatus: QuizWorkflowStatus,
    hasEssayQuestions: z.boolean(),
    students: z.array(GradingQueueStudent),
});
export const GradingQueueResponse = z.object({
    groups: z.array(GradingQueueGroup),
    classes: z.array(z.object({ id: z.string(), name: z.string() })),
    totalPending: z.number().int().nonnegative(),
    truncated: z.boolean(),
});
//# sourceMappingURL=grading.js.map